import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Announcement,
  Card,
  getFormattedLongDate,
  Layout,
  LoaderSkeleton,
  parseLocalDate,
  Pill,
  Search,
  SubscriptionIcon,
  TableData,
  Tabs,
  toast,
} from '@utilities';
import { getSubscriptionType } from '@helpers';

import { readClientSubscription, readClientUsersSubscription } from '@api/client';
import { addUserToSubscription, removeUserFromSubscription } from '@api/user';

import SubscriptionOverview from '../../../../components/SubscriptionOverview';
import { useSubscriptionV2 } from '../../../../utilities/helpers';

import { createUserSubscriptionColumns } from './utilities/helpers';

import styles from './_index.module.scss';

const VerifiedVoicesSubscription = () => {
  const [addingUsers, setAddingUsers] = useState({});
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [removingUsers, setRemovingUsers] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState('overview');
  const [users, setUsers] = useState([]);

  const { id, permissions } = useSelector((state) => state.user?.data);
  const isEditable = permissions?.canEditSurveySubscription;

  const isV2Enabled = useSubscriptionV2();

  useEffect(() => {
    const fetchSubscription = async () => {
      setError(null);
      setIsLoading(true);

      try {
        const [userSubscription, subscriptionData] = await Promise.all([
          readClientUsersSubscription(),
          readClientSubscription(),
        ]);

        setSubscription(subscriptionData[0]);
        setUsers(userSubscription?.results || []);
      } catch (err) {
        setError(`Failed to load subscription info: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscription();
  }, []);

  const tableData = useMemo(
    () =>
      (users || []).map((user) => ({
        current: Number(user.periodCompletesUsed ?? 0),
        id: user.id,
        isSubscribed: !!user.isSubscribed,
        name: [user.firstName, user.lastName].filter(Boolean).join(' ') || '—',
        previous: Number(user.previousPeriodCompletesUsed ?? 0),
        type: user.type || '',
      })),
    [users]
  );

  const filteredData = useMemo(() => {
    const query = (searchQuery || '').trim().toLowerCase();
    if (!query) return tableData;

    const tokens = query.split(/\s+/);
    return tableData.filter((row) => {
      const haystack = `${row.name} ${row.type}`.toLowerCase();
      return tokens.every((token) => haystack.includes(token));
    });
  }, [searchQuery, tableData]);

  const handleAddToSubscription = async (userId) => {
    setAddingUsers((prev) => ({ ...prev, [userId]: true }));
    try {
      await addUserToSubscription({ subscriptionId: subscription.id, userId });

      setUsers((prev) =>
        prev.map((user) => (user.id === userId ? { ...user, isSubscribed: true } : user))
      );

      setSubscription((subscription) => ({
        ...subscription,
        surveyCreatorsCount: (subscription?.surveyCreatorsCount ?? 0) + 1,
      }));

      toast('User added to subscription successfully.', { status: 'success' });
    } catch (err) {
      toast(`Failed to add user: ${err.message}`, { status: 'error' });
    } finally {
      setAddingUsers((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const canUpdateSubscription = users.some((user) => user?.id === id && user?.type === 'admin');
  const handleRemoveFromSubscription = async (userId) => {
    setRemovingUsers((prev) => ({ ...prev, [userId]: true }));
    try {
      await removeUserFromSubscription({ subscriptionId: subscription.id, userId });

      setUsers((prev) =>
        prev.map((user) => (user.id === userId ? { ...user, isSubscribed: false } : user))
      );

      setSubscription((subscription) => ({
        ...subscription,
        surveyCreatorsCount: (subscription?.surveyCreatorsCount ?? 0) - 1,
      }));

      toast('User removed from subscription successfully.', { status: 'success' });
    } catch (err) {
      toast('Failed to remove user from subscription', { status: 'error' });
    } finally {
      setRemovingUsers((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const columns = createUserSubscriptionColumns({
    addingMap: addingUsers,
    canUpdateSubscription,
    onAddToSubscription: handleAddToSubscription,
    onRemoveFromSubscription: handleRemoveFromSubscription,
    removingMap: removingUsers,
    tableData,
  });

  if (isLoading) {
    return (
      <LoaderSkeleton height={115}>
        <rect height="15" rx="2" ry="2" width="200" x="0" y="0" />
        <rect height="15" rx="2" ry="2" width="175" x="0" y="25" />
        <rect height="15" rx="2" ry="2" width="225" x="0" y="50" />
        <rect height="15" rx="2" ry="2" width="200" x="0" y="75" />
        <rect height="15" rx="2" ry="2" width="225" x="0" y="100" />
      </LoaderSkeleton>
    );
  }

  if (error || !subscription || !users) {
    return (
      <div>
        <Announcement
          header="No subscription information available."
          text="Please contact your consultant for more information."
          variant="info"
        />
      </div>
    );
  }

  return (
    <div className={styles['vv-subscription']}>
      <div className={styles['vv-subscription-overview']}>
        {isEditable && <Pill isDisabled={true} value="Admin" />}
      </div>
      {isV2Enabled ? (
        <>
          <Tabs
            onChange={setSelectedTab}
            options={[
              { label: 'Overview', value: 'overview' },
              { label: 'Users', value: 'users' },
            ]}
            value={selectedTab}
          />
          {selectedTab === 'overview' && (
            <div className={styles['vv-subscription-overview']}>
              <Card
                as={Layout.Flex.Column}
                gap="small"
                padding="large"
                statusColor="var(--color-background-success-bold)"
              >
                <h3>Default Subscription</h3>
                <Layout.Flex.Row
                  className={styles['vv-subscription-overview-content']}
                  gap="xx-large"
                >
                  <Layout.Flex.Column className={styles['vv-subscription-overview-section']}>
                    <SubscriptionOverview />
                  </Layout.Flex.Column>
                  <div className={styles['vv-subscription-divider']} />
                  <Layout.Flex.Column
                    className={styles['vv-subscription-overview-section']}
                    gap="small"
                  >
                    <p>
                      Subscription Start Date:{' '}
                      <strong>
                        {subscription?.startDate
                          ? getFormattedLongDate(parseLocalDate(subscription.startDate))
                          : 'N/A'}
                      </strong>
                    </p>
                    <p>
                      Subscription End Date:{' '}
                      <strong>
                        {subscription?.endDate
                          ? getFormattedLongDate(parseLocalDate(subscription.endDate))
                          : 'N/A'}
                      </strong>
                    </p>
                    <p>
                      Subscription Completes:{' '}
                      <strong>{subscription?.completesTotal?.toLocaleString()}</strong>
                    </p>
                    <p className={styles['vv-subscription-icon-row']}>
                      <SubscriptionIcon />
                      <span>
                        Survey Creators: <strong>{subscription.surveyCreatorsCount}</strong>
                      </span>
                    </p>
                  </Layout.Flex.Column>
                </Layout.Flex.Row>
              </Card>
            </div>
          )}
          {selectedTab === 'users' && (
            <div className={styles['vv-subscription-users']}>
              <h2>Users</h2>
              <Search
                className={styles['vv-subscription-search']}
                isSearching={isLoading && searchQuery.trim().length > 0}
                onChange={(query) => setSearchQuery(query)}
                placeholder="Search for a user"
                value={searchQuery}
              />
              {filteredData.length === 0 && searchQuery.trim().length > 0 ? (
                <div className={styles['vv-subscription-empty-message']}>
                  <h3>No results found.</h3>
                  <p>Try refining your search.</p>
                </div>
              ) : (
                <TableData columns={columns} data={filteredData} isLoading={isLoading} />
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <div className={styles['vv-subscription-overview']}>
            <h2>Overview</h2>
            <div>
              <Card>
                <h4>Usage</h4>
                <SubscriptionOverview />
              </Card>
              <Card>
                <h4>Verified Voices Plan</h4>
                <p>
                  Completes per {getSubscriptionType({ type: subscription.type })}:{' '}
                  <strong>{subscription?.currentPeriod?.allowedCompletes}</strong>
                </p>
                <p className={styles['vv-subscription-icon-row']}>
                  <SubscriptionIcon />
                  <span>
                    Survey Creators: <strong>{subscription.surveyCreatorsCount}</strong>
                  </span>
                </p>
                <p>
                  Viewers: <strong>{subscription.surveyViewersCount}</strong>
                </p>
              </Card>
            </div>
          </div>
          <div className={styles['vv-subscription-users']}>
            <h2>Users</h2>
            <Search
              className={styles['vv-subscription-search']}
              isSearching={isLoading && searchQuery.trim().length > 0}
              onChange={(query) => setSearchQuery(query)}
              placeholder="Search"
              value={searchQuery}
            />
            {filteredData.length === 0 && searchQuery.trim().length > 0 ? (
              <div className={styles['vv-subscription-empty-message']}>
                <h3>No results found.</h3>
                <p>Try refining your search.</p>
              </div>
            ) : (
              <TableData columns={columns} data={filteredData} isLoading={isLoading} />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default VerifiedVoicesSubscription;
